import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { collection, query, where, getDocs, doc, deleteDoc } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { API_BASE_URL } from '../../constants';
import EditProjectModal from '../../components/EditProjectModal/EditProjectModal';
import AddToCartButton from '../../components/Cart/AddToCartButton';
import { useCart } from '../../context/CartContext';
import './ProjectDetailPage.css';

const getFileNameFromUrl = (url) => {
  if (!url) {
    return null;
  }

  try {
    const [pathPart] = url.split('?');
    const segments = pathPart.split('/');
    const lastSegment = segments.pop();
    return lastSegment || null;
  } catch (error) {
    console.warn('Failed to parse filename from URL:', url, error);
    return null;
  }
};

function ProjectDetailPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const projectId = searchParams.get('id');
  
  const [project, setProject] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  const { hasPurchased, currentUser: cartUser } = useCart();
  const isPurchased = project ? hasPurchased(project.projectId) : false;

  const imageUrls = project?.imageUrls?.length
    ? project.imageUrls
    : project?.imageUrl
    ? [project.imageUrl]
    : [];

  const imageFileNames = project?.imageFileNames?.length
    ? project.imageFileNames
    : project?.imageFileName
    ? [project.imageFileName]
    : [];

  const hasImages = imageUrls.length > 0;
  const hasMultipleImages = imageUrls.length > 1;
  const safeImageIndex = hasImages
    ? Math.min(activeImageIndex, imageUrls.length - 1)
    : 0;
  const currentImageUrl = hasImages ? imageUrls[safeImageIndex] : null;
  const currentImageFileName = hasImages
    ? imageFileNames[safeImageIndex] || getFileNameFromUrl(imageUrls[safeImageIndex])
    : null;

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!projectId) {
      navigate('/marketplace');
      return;
    }
    fetchProject();
  }, [projectId]);

  useEffect(() => {
    setActiveImageIndex(0);
  }, [project?.id]);

  const goToPreviousImage = () => {
    if (!hasMultipleImages) return;
    setActiveImageIndex((prev) => (prev === 0 ? imageUrls.length - 1 : prev - 1));
  };

  const goToNextImage = () => {
    if (!hasMultipleImages) return;
    setActiveImageIndex((prev) => (prev === imageUrls.length - 1 ? 0 : prev + 1));
  };

  const fetchProject = async () => {
    try {
      const q = query(
        collection(db, 'marketplace-projects'),
        where('projectId', '==', projectId)
      );
      const snapshot = await getDocs(q);
      
      if (!snapshot.empty) {
        const doc = snapshot.docs[0];
        setProject({ id: doc.id, ...doc.data() });
      } else {
        alert('Project not found');
        navigate('/marketplace');
      }
    } catch (error) {
      console.error('Error fetching project:', error);
      alert('Failed to load project');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this project?')) {
      return;
    }

    setIsEditOpen(false);
    setDeleting(true);
    try {
      // Delete from Firestore
      await deleteDoc(doc(db, 'marketplace-projects', project.id));
      
      // TODO: Call backend to delete from GCS
      const token = await auth.currentUser?.getIdToken();
      const response = await fetch(`${API_BASE_URL}/api/delete-project/${project.folderName}`, {
        method: 'DELETE',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      
      if (!response.ok) {
        console.error('Failed to delete files from GCS');
      }
      
      alert('Project deleted successfully');
      navigate('/marketplace');
    } catch (error) {
      console.error('Error deleting project:', error);
      alert('Failed to delete project');
    } finally {
      setDeleting(false);
    }
  };

  const handleProjectUpdated = (updatedProject) => {
    setProject(updatedProject);
  };

  const downloadFile = async (fileType, options = {}) => {
    const { imageIndex = safeImageIndex } = options;
    try {
      let fileName;
      if (fileType === 'STEP') {
        fileName = project.stepFileName;
      } else if (fileType === 'XML') {
        fileName = project.xmlFileName;
      } else if (fileType === 'Image' && hasImages) {
        const targetUrl = imageUrls[imageIndex] || imageUrls[0];
        fileName = imageFileNames[imageIndex] || getFileNameFromUrl(targetUrl);
      }

      if (!fileName) {
        alert('File not found');
        return;
      }

      const url = `${API_BASE_URL}/api/download-file?folderName=${encodeURIComponent(project.folderName)}&fileName=${encodeURIComponent(fileName)}`;
      
      // Fetch the file as a blob (with auth)
      const token = await auth.currentUser?.getIdToken();
      const response = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) {
        throw new Error('Download failed');
      }
      
      const blob = await response.blob();
      
      // Create a download link with the blob
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName; // This will be the filename for download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the blob URL
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Download error:', error);
      alert('Failed to download file');
    }
  };

  if (loading) {
    return (
      <div className="project-detail">
        <div className="project-detail__loading">Loading project...</div>
      </div>
    );
  }

  if (!project) {
    return null;
  }

  const isOwner = currentUser && currentUser.uid === project.ownerId;

  return (
    <div className="project-detail">
      <div className="project-detail__header">
        <button 
          className="project-detail__back-btn"
          onClick={() => navigate('/marketplace')}
        >
          Back to Marketplace
        </button>
        {isOwner && (
          <button
            className="project-detail__edit-btn"
            onClick={() => setIsEditOpen(true)}
            disabled={deleting}
          >
            ✏️ Edit Project
          </button>
        )}
      </div>

      <div className="project-detail__container">
        <div className="project-detail__image-section">
          {hasImages ? (
            <div className="project-detail__carousel">
              <div className="project-detail__carousel-frame">
                {hasMultipleImages && (
                  <button
                    type="button"
                    className="project-detail__carousel-nav project-detail__carousel-nav--prev"
                    onClick={goToPreviousImage}
                    aria-label="View previous image"
                  >
                    ‹
                  </button>
                )}

                <img
                  src={currentImageUrl}
                  alt={`${project.name} preview ${safeImageIndex + 1}`}
                  className="project-detail__image"
                />

                {hasMultipleImages && (
                  <button
                    type="button"
                    className="project-detail__carousel-nav project-detail__carousel-nav--next"
                    onClick={goToNextImage}
                    aria-label="View next image"
                  >
                    ›
                  </button>
                )}
              </div>

              <div className="project-detail__carousel-meta">
                {hasMultipleImages && (
                  <span className="project-detail__carousel-counter">
                    {safeImageIndex + 1} / {imageUrls.length}
                  </span>
                )}
                {currentImageFileName && (
                  <span className="project-detail__carousel-filename">{currentImageFileName}</span>
                )}
              </div>

              {hasMultipleImages && (
                <div className="project-detail__carousel-thumbs">
                  {imageUrls.map((url, index) => (
                    <button
                      key={`${url}-${index}`}
                      type="button"
                      className={`project-detail__thumb ${index === safeImageIndex ? 'project-detail__thumb--active' : ''}`}
                      onClick={() => setActiveImageIndex(index)}
                      aria-label={`View image ${index + 1}`}
                    >
                      <img src={url} alt={`Thumbnail ${index + 1}`} />
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div 
              className="project-detail__placeholder"
              style={{ backgroundColor: project.imageColor || '#6366f1' }}
            >
              {project.name?.charAt(0).toUpperCase() || 'P'}
            </div>
          )}
        </div>

        <div className="project-detail__info-section">
          <h1 className="project-detail__title">{project.name}</h1>
          
          <div className="project-detail__meta">
            <p className="project-detail__creator">
              <strong>Created by:</strong> {project.ownerName || 'Anonymous'}
            </p>
            <p className="project-detail__date">
              <strong>Date:</strong> {project.createdAt?.toDate?.()?.toLocaleDateString() || 'Recently'}
            </p>
          </div>

          {project.description && (
            <div className="project-detail__description">
              <h2>Description</h2>
              <p>{project.description}</p>
            </div>
          )}

          <div className="project-detail__price-section">
            <span className="project-detail__price">
              {project.price && project.price > 0
                ? `$${Number(project.price).toFixed(2)}`
                : 'Free'}
            </span>
            {!isOwner && (
              <AddToCartButton project={project} />
            )}
          </div>

          <div className="project-detail__files">
            <h2>Project Files</h2>
            <div className="project-detail__file-list">
              {hasImages && (
                <div className="project-detail__file-item">
                  <span className="project-detail__file-icon" aria-hidden="true">IMG</span>
                  <span className="project-detail__file-name">
                    Project Image{hasMultipleImages ? `s (${imageUrls.length})` : ''}
                  </span>
                  {isOwner && (
                    <button 
                      className="project-detail__download-btn"
                      onClick={() => downloadFile('Image', { imageIndex: safeImageIndex })}
                    >
                      Download
                    </button>
                  )}
                </div>
              )}
              
              <div className="project-detail__file-item">
                <span className="project-detail__file-icon" aria-hidden="true">STEP</span>
                <span className="project-detail__file-name">STEP File</span>
                {isOwner || isPurchased ? (
                  <button 
                    className="project-detail__download-btn"
                    onClick={() => downloadFile('STEP')}
                  >
                    Download
                  </button>
                ) : (
                  <span className="project-detail__file-locked">
                    {project.price > 0 ? 'Purchase to Download' : 'Owner Only'}
                  </span>
                )}
              </div>
              
              <div className="project-detail__file-item">
                <span className="project-detail__file-icon" aria-hidden="true">XML</span>
                <span className="project-detail__file-name">XML File</span>
                {isOwner || isPurchased ? (
                  <button 
                    className="project-detail__download-btn"
                    onClick={() => downloadFile('XML')}
                  >
                    Download
                  </button>
                ) : (
                  <span className="project-detail__file-locked">
                    {project.price > 0 ? 'Purchase to Download' : 'Owner Only'}
                  </span>
                )}
              </div>
            </div>
          </div>

        </div>
      </div>

      {isOwner && isEditOpen && (
        <EditProjectModal
          project={project}
          onClose={() => setIsEditOpen(false)}
          onProjectUpdated={handleProjectUpdated}
          onDelete={handleDelete}
          deleting={deleting}
        />
      )}
    </div>
  );
}

export default ProjectDetailPage;
